// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CheckboxStyle } from "@insite/mobius/Checkbox";
import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import CheckboxCheck from "@synd/XDL/Icons/CheckboxCheck";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xBaseTheme } from "@synd/XDL/types";
import color from "@synd/XDL/Utilities/color";
import { css } from "styled-components";

export const CheckboxInputSelect = `span[role="checkbox"]`;

export const CheckboxCheckedSelector = `
    &[aria-checked="true"]:not([aria-disabled="true"]),
    &[aria-checked="mixed"]:not([aria-disabled="true"])
`;

export const CheckboxDefaultSelector = `&[aria-checked="false"]:not([aria-disabled="true"])`;
export const CheckboxDisabledSelector = `&[aria-disabled="true"]`;

const defaultStateStyles = css`
    & ${CheckboxInputSelect} {
        height: 13px;
        min-width: 13px;
        width: 13px;
        border-color: ${color("common.ui4")};
        border-radius: 2px;
        background: ${color("common.ui2")};
        color: ${color("common.ui1")};
        &:focus {
            box-shadow: none;
        }
        & + label {
            margin-left: 8px;
        }
    }
`;

const checkedStyle = css`
    & ${CheckboxInputSelect} {
        ${CheckboxCheckedSelector} {
            background: ${color("common.ui11")};
            border-color: ${color("common.ui11")};
            color: ${color("common.ui1")};
            justify-content: center;
        }
    }
`;
const hoverStyle = css`
    &:hover {
        ${CheckboxInputSelect} {
            ${CheckboxCheckedSelector} {
                background: ${color("common.ui10")};
                border-color: ${color("common.ui10")};
            }
            ${CheckboxDefaultSelector} {
                border-color: ${color("common.ui10")};
            }
        }
    }
`;

const disabledStyle = css`
    ${CheckboxInputSelect} {
        ${CheckboxDisabledSelector} {
            background: ${color("common.ui2", 0.33)};
            border: 1px solid ${color("common.ui4", 0.33)};
            cursor: not-allowed;
            justify-content: center;
        }
    }
`;

export default {
    defaultProps: {
        ...CheckboxPresentationPropsDefault,
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: fontConfig.xSmallSingleLine.size,
            weight: fontConfig.weight.regular,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                user-select: none;
                &:empty {
                    display: none;
                }
            `,
        },
        iconProps: {
            src: CheckboxCheck,
            size: 13,
            ...({ height: 7, width: 9 } as any),
        },
        css: css`
            &:not(:disabled) {
                cursor: pointer;
            }
            &&&[disabled] {
                cursor: not-allowed;
            }

            ${({ _labelPosition }: any) =>
                _labelPosition === "left" &&
                css`
                    margin-left: 0;
                `}

            ${defaultStateStyles}
            ${hoverStyle}
            ${checkedStyle}
            ${disabledStyle}
        `,
    },
    groupDefaultProps: {
        labelProps: {
            size: fontConfig.small.size,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.small.lineHeight};
                &:empty {
                    display: none;
                }
            `,
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                &:first-child {
                    margin-bottom: 12px;
                }
                &:empty {
                    display: none;
                }
            }
            ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
                margin-top: 12px;
            }
        `,
    },
} as xBaseTheme["checkbox"];
